import { z } from 'zod';
import { formatZodIssues } from './zod-error';

export const ruleIdSchema = z.enum([
  'datacenterAsn',
  'missingTtclid',
  'webdriverFlag',
  'headlessUa',
  'noInteraction',
  'mobileMismatch',
  'notInGeoAllow',
  'isDesktop',
  'challengeFailed',
  'noMotionSensors',
  'noMediaDevices',
  'platformUaMismatch',
  'noStorage',
  'lowPerfJitter',
  'googlePayNotReady',

  // Mobile-specific
  'desktopGpuOnMobile',
  'noTouchOnMobile',
  'lowDprOnMobile',
  'nonCoarsePointer',
  'noVibrateOnMobile',
  'noOrientationOnMobile',
  'notInAppWebview',
  // Advanced (driver / hints / behavior)
  'hasDriverMarker',
  'nativeFnTampered',
  'missingClientHints',
  'clientHintsMismatch',
  'syntheticTouch',
  'zeroSpeechVoices',
  'cssFeatureMismatch',
  'lowMouseEntropy',
  // --- v2: Probe cross-check rules ---
  'timezoneMismatch',
  'languageMismatch',
  'probeVerifyIpChange',
  // --- v2: New client signal rules ---
  'rafTimingFlat',
  'canvasNoise',
  'lowScrollEntropy',
  'lowClickEntropy',
  // --- v3: OSS-backed signals ---
  // BotD (FingerprintJS open-source) client-side bot verdict
  'botdBot',
  // Offline ASN→type classification: proxy / anonymizer IP (datacenterAsn stays
  // for hosting/datacenter; proxyIp covers VPN/proxy/anonymizer ASNs)
  'proxyIp',
  // Free-tier TLS/HTTP consistency: modern mobile UA on ancient TLS/HTTP
  'tlsHttpMismatch',
  // --- v4: device identity ---
  // One persistent device (stored UUID) seen across >= crossLinkMinDistinct
  // distinct links — normal ad traffic lands on exactly one link, so a device
  // spanning several links is recon (competitor / reviewer / scraper).
  'crossLinkDevice',
  // --- observation-only (weight 0) ---
  // Previously-collected-but-unused fingerprint signals, wired up at weight 0 so
  // their hit appears in audits.reasons for analysis WITHOUT moving pass/block.
  // (Weight MUST stay 0, not 1: a lone weight-1 marker stacked on a rule that puts
  // a real visitor exactly on passThreshold tips the verdict block. Promote to a
  // real weight only with data behind it.)
  'notificationPermMismatch',
  'noPlatformAuthenticator',
  // 指纹收集超时降级(恶劣 in-app webview)——observation-only,仅标记不改判定
  'signalCollectTimeout',
  // --- v5: edge token cross-checks (stateless, production-remote-integration) ---
  // The middleware signs the HTML request's edge signals into a token the client
  // returns at verify; these compare "HTML 时刻" vs "verify 时刻" without storage.
  // verify 请求未带有效 edge token(缓存 HTML / bfcache / 绕过落地页直打接口)。
  // 弱信号:合法的缓存场景存在,不能单独拦截。
  'edgeTokenMissing',
  // HTML 请求与 verify 之间发生真实网络切换(国家或 ASN 变化,非同运营商轮换)。
  'edgeIpDrift',
  // HTML 请求到 verify 的间隔过短 —— 真人要等页面渲染+点击,自动化不需要。
  'edgeVerifyTooFast',
  // --- 严格模式专属(见 rulesConfigSchema.strictMode) ---
  'staticDevice',
  'sameLinkReentry',
]);

export type RuleId = z.infer<typeof ruleIdSchema>;

// Current rule IDs, for dropping stale keys (see dropUnknownRuleKeys).
const RULE_ID_SET: ReadonlySet<string> = new Set(ruleIdSchema.options);

// A config stored before a rule was renamed/removed still carries the old key.
// Drop unknown keys BEFORE the strict z.record(ruleId) check so parsing never
// throws on a stale config — otherwise loadRules silently falls back to
// DEFAULT_RULES and wipes the entire production config.
const dropUnknownRuleKeys = (v: unknown): unknown =>
  v && typeof v === 'object' && !Array.isArray(v)
    ? Object.fromEntries(Object.entries(v).filter(([k]) => RULE_ID_SET.has(k)))
    : v;

export const rulesConfigSchema = z.object({
  // 分数空间上限放宽到 500(起始分与通过阈值同一量纲),便于按需放大评分粒度。
  passThreshold: z.number().min(0).max(500).default(60),
  startScore: z.number().min(0).max(500).default(100),
  geoAllowlist: z.array(z.string()).default(['SG', 'MY', 'TH', 'ID', 'VN']),
  requireChallenge: z.boolean().default(true),
  // 链接级验证策略(前端 use-cloak 两档语义):
  //   challenge = 显示按钮、点击才跳,但加载即后台预热 verify → 点击瞬时跳。
  //               预热是 challenge 的固有优化(非独立模式),cloak 语义=点击才跳,不变;
  //   auto      = 加载即自动 verify,pass 免点击直跳、block 停留(信任引擎判定拦爬虫)。
  // requireChallenge 保留作向后兼容(旧前端 bundle 读它);新前端优先读 verifyStrategy,
  // buildPublicConfig 会由 verifyStrategy 派生 requireChallenge 一并下发。
  // .catch:遗留的 'preheat'(折叠进 challenge 前存的)或任何未知值优雅回落 challenge,
  // 绝不让单字段非法把整条 rules 配置 parse 失败(loadRules 会退回 DEFAULT_RULES)。
  // 回落到 challenge 行为等价 —— challenge 现在本就自带预热。
  verifyStrategy: z.enum(['challenge', 'auto']).default('challenge').catch('challenge'),
  challengeTimeoutMs: z.number().min(500).max(60_000).default(8000),
  // Redirect URLs (empty = no target; verify fails loud with 500 rather than
  // falling back — link-level config overrides these per link).
  realUrl: z.string().default(''),
  decoyUrl: z.string().default(''),
  // block 判定后是否仍下发 decoy 跳转。false 时 verify 返回空 redirect，
  // 被拦访客停留在模板页（连 decoy URL 都拿不到）。默认 true 向后兼容。
  redirectOnBlock: z.boolean().default(true),
  // 跳转真实页时是否留在当前(应用内)浏览器。false=默认:在 FB/TikTok 等 in-app
  // webview 里用 chrome intent / x-safari scheme 把访客踢到系统浏览器 —— PWA 安装
  // 与 Chrome 唤起类落地页需要这个。true=内联跳转,不做 scheme breakout。
  //
  // 为什么需要开关:被踢进 Chrome 之后,下游 DC 中间页判定 isChromeBrowser 为真,
  // 直接跳过回流页逻辑,于是「真实链接=回流链接」的客户永远等不到回流页弹出。
  // 这类链接把它打开,访客留在 webview 里,回流页才会按预期出现。
  keepInAppBrowser: z.boolean().default(false),
  // crossLinkDevice fires when one device's distinct-link count (incl. current)
  // reaches this. 2 = flag any device seen on a second link.
  crossLinkMinDistinct: z.number().int().min(2).max(50).default(2),
  // 严格模式总开关:staticDevice / sameLinkReentry 只在它为 true 时参与判定,
  // enabled[] 只是它内部的二级微调(引擎的缺键语义是「启用」,关不掉这两条)。
  strictMode: z.boolean().default(false),
  sameLinkReentryMin: z.number().int().min(2).max(100).default(10),
  weights: z.preprocess(dropUnknownRuleKeys, z.record(ruleIdSchema, z.number())).default({
    datacenterAsn: 60,
    missingTtclid: 30,
    webdriverFlag: 80,
    headlessUa: 80,
    noInteraction: 40,
    mobileMismatch: 20,
    notInGeoAllow: 100,
    isDesktop: 80,
    challengeFailed: 70,
    noMotionSensors: 30,
    noMediaDevices: 20,
    platformUaMismatch: 30,
    noStorage: 40,
    lowPerfJitter: 25,
    desktopGpuOnMobile: 60,
    noTouchOnMobile: 50,
    lowDprOnMobile: 25,
    nonCoarsePointer: 30,
    noVibrateOnMobile: 20,
    noOrientationOnMobile: 25,
    notInAppWebview: 30,
    hasDriverMarker: 80,
    nativeFnTampered: 70,
    missingClientHints: 20,
    clientHintsMismatch: 40,
    syntheticTouch: 50,
    zeroSpeechVoices: 25,
    cssFeatureMismatch: 30,
    lowMouseEntropy: 40,
    // v2 rules
    timezoneMismatch: 50,
    languageMismatch: 25,
    probeVerifyIpChange: 70,
    rafTimingFlat: 30,
    // 隐私浏览器(Brave / Firefox RFP)会主动注入 canvas 噪声,这些是真人;
    // 保留作为弱信号,不让它单独压垮一个真实访客。
    canvasNoise: 20,
    lowScrollEntropy: 20,
    lowClickEntropy: 20,
    googlePayNotReady: 0,

    // v3 OSS-backed rules
    botdBot: 70,
    proxyIp: 60,
    tlsHttpMismatch: 20,
    // v4: cross-link recon — 弱信号，需与其他信号叠加才致拦截（默认 10，不单独 block）
    crossLinkDevice: 10,
    // observation-only — weight 0: recorded in reasons for analysis, MUST NOT move
    // the verdict. (Was 1, which looked harmless — "100-1=99" — but a lone weight-1
    // marker stacked on a rule that lands a real visitor exactly on passThreshold
    // tipped 60→59 = block. Observed ~5 real iOS/KL visitors/wk mis-blocked this way.
    // Observation markers score 0; promote to a real weight only with data behind it.)
    notificationPermMismatch: 0,
    noPlatformAuthenticator: 0,
    signalCollectTimeout: 0,
    // v5: edge token cross-checks — drift/too-fast 是强信号;missing 是弱信号
    // (合法缓存/bfcache 场景存在,且 token 未铺开前所有流量都会命中)
    edgeTokenMissing: 15,
    edgeIpDrift: 70,
    edgeVerifyTooFast: 40,
    staticDevice: 25,
    sameLinkReentry: 45,
  }),
  enabled: z.preprocess(dropUnknownRuleKeys, z.record(ruleIdSchema, z.boolean())).default({
    datacenterAsn: true,
    missingTtclid: true,
    webdriverFlag: true,
    headlessUa: true,
    noInteraction: true,
    mobileMismatch: true,
    notInGeoAllow: true,
    isDesktop: true, // 仅放行 FB/TikTok 广告 in-app 流量(移动端),默认屏蔽 PC
    challengeFailed: true,
    noMotionSensors: true,
    noMediaDevices: true,
    platformUaMismatch: true,
    noStorage: true,
    lowPerfJitter: true,
    desktopGpuOnMobile: true,
    noTouchOnMobile: true,
    lowDprOnMobile: true,
    nonCoarsePointer: true,
    noVibrateOnMobile: true,
    noOrientationOnMobile: true,
    notInAppWebview: true, // ON — ad clicks arrive inside the platform in-app webview (FB/IG/TikTok); a visitor outside any in-app webview is suspect
    hasDriverMarker: true,
    nativeFnTampered: true,
    missingClientHints: true,
    clientHintsMismatch: true,
    syntheticTouch: true,
    zeroSpeechVoices: true,
    cssFeatureMismatch: true,
    lowMouseEntropy: true,
    // v2 rules
    timezoneMismatch: true,
    languageMismatch: true,
    probeVerifyIpChange: true,
    rafTimingFlat: true,
    canvasNoise: true,
    lowScrollEntropy: true,
    lowClickEntropy: true,
    googlePayNotReady: true,

    // v3 OSS-backed rules ship OFF — enable after audit-replay validation (see
    // change harden-cloak-detection migration plan, phase 2/4).
    botdBot: false,
    proxyIp: false,
    tlsHttpMismatch: false,
    // v4: cross-link recon — ON by default (user-requested anti-recon signal)
    crossLinkDevice: true,
    // observation-only — ON so they record hits; weight 0 keeps them verdict-neutral
    notificationPermMismatch: true,
    noPlatformAuthenticator: true,
    signalCollectTimeout: true,
    // v5: edge token cross-checks — drift/too-fast ON(token 在场才评估,天然安全);
    // missing 也 ON,但 weight 15 不会单独翻转判定。
    edgeTokenMissing: true,
    edgeIpDrift: true,
    edgeVerifyTooFast: true,
    staticDevice: true,
    sameLinkReentry: true,
  }),
  // Rule clusters: correlated weak signals whose combined deduction is capped at
  // `clusterCaps[name]` so they can't stack into a block on a real low-end device.
  // The engine deducts min(sum_of_hits_in_cluster, cap) per cluster. Rules NOT in
  // any cluster are deducted at full weight independently.
  clusterCaps: z.record(z.string(), z.number()).default({
    mobileCapabilityWeak: 40,
    // Geo-consistency signals (language + timezone vs IP country) are correlated —
    // both测的是「设备地区 vs IP 国家」是否一致。对通过了主动挑战的真人(点/触 CTA,
    // bot 不点)只 cap 在 40,避免 VPN / 旅居真人被这两条叠加(25+50=75)误杀。
    // 仅在 passedActiveChallenge 时由引擎应用(见 engine.ts cluster cap 段)。
    geoConsistency: 40,
  }),
});

export type RulesConfig = z.infer<typeof rulesConfigSchema>;

/** 链接级验证策略枚举(challenge/auto)的单一真源,供前后端/后台复用。 */
export type VerifyStrategy = RulesConfig['verifyStrategy'];

export const DEFAULT_RULES: RulesConfig = rulesConfigSchema.parse({});

/**
 * 把 link 级 partial 覆盖合并到基线配置之上(标量字段整体替换,record 字段按键合并)。
 * local 后端的基线是 D1 平台规则,remote 后端的基线是内置 DEFAULT_RULES ——
 * 合并一律发生在 worker 侧(remote /config 只返回原样存储的覆盖项,契约 v2)。
 */
export function mergeRulesOverrides(
  base: RulesConfig,
  overrides: Partial<RulesConfig>,
): RulesConfig {
  return {
    passThreshold: overrides.passThreshold ?? base.passThreshold,
    startScore: overrides.startScore ?? base.startScore,
    geoAllowlist: overrides.geoAllowlist ?? base.geoAllowlist,
    requireChallenge: overrides.requireChallenge ?? base.requireChallenge,
    verifyStrategy: overrides.verifyStrategy ?? base.verifyStrategy,
    challengeTimeoutMs: overrides.challengeTimeoutMs ?? base.challengeTimeoutMs,
    crossLinkMinDistinct: overrides.crossLinkMinDistinct ?? base.crossLinkMinDistinct,
    strictMode: overrides.strictMode ?? base.strictMode,
    sameLinkReentryMin: overrides.sameLinkReentryMin ?? base.sameLinkReentryMin,
    realUrl: overrides.realUrl ?? base.realUrl,
    decoyUrl: overrides.decoyUrl ?? base.decoyUrl,
    redirectOnBlock: overrides.redirectOnBlock ?? base.redirectOnBlock,
    keepInAppBrowser: overrides.keepInAppBrowser ?? base.keepInAppBrowser,
    weights: { ...base.weights, ...overrides.weights },
    enabled: { ...base.enabled, ...overrides.enabled },
    clusterCaps: { ...base.clusterCaps, ...overrides.clusterCaps },
  };
}

/** 覆盖 record 字段(按键合并/按键 diff),其余为标量/数组(整体替换)。 */
export const OVERRIDE_RECORD_KEYS = ['weights', 'enabled', 'clusterCaps'] as const;

/**
 * diff/merge 刻意跳过的字段(链接级覆盖 round-trip 有意丢弃):
 * realUrl/decoyUrl 由 link 独立列托底(verify 用 link.realUrl || rules.realUrl),
 * 覆盖 JSON 从不经 UI 写入它们;requireChallenge 由 verifyStrategy 统一派生,不单独覆盖。
 */
export const OVERRIDE_EXCLUDED_KEYS = ['realUrl', 'decoyUrl', 'requireChallenge'] as const;

/**
 * mergeRulesOverrides 的逆运算:算出 effective 相对 base 的最小 partial 覆盖。
 * 按 Object.keys(base) 驱动(而非手工枚举),这样 RulesConfig 新增标量字段会自动被 diff,
 * 不会静默漏掉;record 字段按键收录差异项;豁免字段见 OVERRIDE_EXCLUDED_KEYS。
 */
export function diffRulesOverrides(
  effective: RulesConfig,
  base: RulesConfig,
): Partial<RulesConfig> {
  const out: Record<string, unknown> = {};
  const excluded = new Set<string>(OVERRIDE_EXCLUDED_KEYS);
  const records = new Set<string>(OVERRIDE_RECORD_KEYS);
  for (const key of Object.keys(base) as (keyof RulesConfig)[]) {
    if (excluded.has(key)) continue;
    const ev = effective[key];
    const bv = base[key];
    if (records.has(key)) {
      const rec = diffRecord(ev as Record<string, unknown>, bv as Record<string, unknown>);
      if (rec) out[key] = rec;
    } else if (Array.isArray(bv)) {
      if (JSON.stringify(ev) !== JSON.stringify(bv)) out[key] = ev;
    } else if (ev !== bv) {
      out[key] = ev;
    }
  }
  return out as Partial<RulesConfig>;
}

function diffRecord<V>(
  effective: Record<string, V>,
  base: Record<string, V>,
): Record<string, V> | undefined {
  const out: Record<string, V> = {};
  for (const [k, v] of Object.entries(effective)) {
    if (v !== base[k]) out[k] = v;
  }
  return Object.keys(out).length ? out : undefined;
}

/* ── RAW JSON 模式(后台「平台默认规则」页) ──────────────────────────────
 * 用途:在一个环境导出全量 JSON,切到另一个环境(Beta / DC / RB 各自独立
 * Worker + D1)粘贴保存,以此同步检测策略。
 *
 * 载荷刻意是**全量绝对值**而非「相对 DEFAULT_RULES 的最小 diff」:三个 Worker
 * 各自独立部署,内置默认值随时可能不同版本。差值编码会把「我没改过这项」和
 * 「这个环境的默认是多少」混为一谈 —— 源环境默认 55、目标环境默认 60 时,
 * 一份不含该字段的 diff 会在两边解读出不同结果,且无从察觉。绝对值不依赖任
 * 何一端的默认值。
 */

/** 导出:全量配置 + 2 空格缩进 + 末尾换行,便于整段复制粘贴与手工编辑。 */
export function serializeRulesConfig(config: RulesConfig): string {
  return `${JSON.stringify(rulesConfigSchema.parse(config), null, 2)}\n`;
}

export type ParseRulesJsonResult = { ok: true; config: RulesConfig } | { ok: false; error: string };

/**
 * 导入:解析并校验一份粘贴进来的配置 JSON。
 *
 * 语义是**全量替换** —— 缺失字段回落 `DEFAULT_RULES`(schema 的 `.default()`),
 * 而不是保留目标环境现值。「同步」的定义就是让两边一致,按字段合并会在目标
 * 环境留下源环境没有的旧值。
 *
 * 校验失败一律返回可读错误而非抛出:RAW 模式没有 diff 预览,这条错误信息是
 * 粘错内容时唯一的反馈,必须指名道姓到字段路径。
 */
export function parseRulesJson(text: string): ParseRulesJsonResult {
  if (!text.trim()) return { ok: false, error: 'JSON 内容为空' };

  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch (e) {
    return { ok: false, error: `JSON 语法错误：${e instanceof Error ? e.message : String(e)}` };
  }

  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return { ok: false, error: 'JSON 顶层必须是一个对象（{ ... }）' };
  }

  // 拼错的顶层字段名(passThresold)会被 zod 静默 strip,配置随后按默认值保存,
  // 用户全程收不到反馈。这里先拦下 —— 拼错没有合法用途。
  // 注意刻意**不**对 weights/enabled 内的未知规则 id 做同样处理:三个 Worker 独立
  // 部署,源环境比目标新时必然带来目标不认识的规则 id,拒收整份会让跨环境同步卡死,
  // 丢弃它们再保住其余配置才是对的(见 dropUnknownRuleKeys)。
  const unknownKeys = Object.keys(raw).filter((k) => !(k in DEFAULT_RULES));
  if (unknownKeys.length > 0) {
    return { ok: false, error: `存在未知字段（请检查拼写）：${unknownKeys.join('、')}` };
  }

  const parsed = rulesConfigSchema.safeParse(raw);
  if (!parsed.success) {
    return { ok: false, error: `配置校验失败 —— ${formatZodIssues(parsed.error.issues)}` };
  }

  // zod 的 .default() 只在字段**整体**缺失时生效,所以一份只列了部分权重的 JSON
  // (旧快照 / 手工精简)会带着残缺 record 通过校验。而 worker 的 loadRules 读取时
  // 会用 DEFAULT_RULES 把 record 补回全量 —— 不在这里补齐,「提交的」与「实际生效
  // 的」就是两份东西,回显也就骗人。回落 DEFAULT 在 record 内部同样成立。
  return {
    ok: true,
    config: {
      ...parsed.data,
      weights: { ...DEFAULT_RULES.weights, ...parsed.data.weights },
      enabled: { ...DEFAULT_RULES.enabled, ...parsed.data.enabled },
      clusterCaps: { ...DEFAULT_RULES.clusterCaps, ...parsed.data.clusterCaps },
    },
  };
}

/**
 * Cluster membership: which rules belong to which capped cluster.
 * `mobileCapabilityWeak` groups the low-signal mobile-capability checks. On a
 * cheap/old real Android, several of these can fire together (no vibrate, no
 * orientation, low DPR, no TTS voices, no media devices) and previously summed
 * to a block. The engine caps the cluster's total deduction at
 * `clusterCaps.mobileCapabilityWeak`.
 */
export const RULE_CLUSTERS: Record<string, RuleId[]> = {
  mobileCapabilityWeak: [
    'noVibrateOnMobile',
    'noOrientationOnMobile',
    'lowDprOnMobile',
    'zeroSpeechVoices',
    'noMediaDevices',
    'tlsHttpMismatch',
  ],
  // language + timezone both测 device-locale vs IP-country。高度相关,叠加易把
  // VPN / 旅居真人压垮。引擎仅对通过主动挑战的访客(强真人信号)cap 此 cluster。
  geoConsistency: ['languageMismatch', 'timezoneMismatch'],
};

export const DATACENTER_ASN_KEYWORDS = [
  'amazon',
  'aws',
  'google',
  'gcp',
  'microsoft',
  'azure',
  'digitalocean',
  'linode',
  'ovh',
  'hetzner',
  'vultr',
  'oracle',
  'cloudflare',
  'alibaba',
  'tencent',
  'choopa',
  'leaseweb',
];

export const PROXY_ORG_KEYWORDS = [
  'vpn',
  'proxy',
  'anonymizer',
  'anonymous',
  ' tor ',
  'tor-exit',
  'tor exit',
  'mullvad',
  'nordvpn',
  'expressvpn',
  'privatevpn',
  'private internet access',
  'datacamp', // CDN77 / VPN backbone frequently used by consumer VPNs
];

export const DESKTOP_GPU_KEYWORDS = [
  'geforce',
  'nvidia',
  'radeon',
  'intel(r) hd',
  'intel(r) iris',
  'intel hd',
  'intel iris',
  'intel uhd',
  'amd ryzen',
  'rtx',
  'gtx',
  'quadro',
  'virtualbox',
  'vmware',
  'parallels',
  'swiftshader', // software renderer used by headless
  'llvmpipe', // mesa software
];

export const MOBILE_GPU_KEYWORDS = [
  'adreno',
  'mali',
  'powervr',
  'apple gpu',
  'apple a', // Apple A-series
  'apple m1', // iPad
  'apple m2',
];

export const HEADLESS_UA_PATTERNS = [
  'HeadlessChrome',
  'PhantomJS',
  'Selenium',
  'puppeteer',
  'playwright',
  'electron',
];
